import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowLeft, Sparkles, Play, Pause, Trash2, FileText, Image, Video,
  Calendar, Clock, ChevronRight, Loader2, Download, Eye, RefreshCw
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { ScrollArea } from "../components/ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../components/ui/dialog";
import { toast } from "sonner";
import axios from "axios";
import Sidebar from "../components/Sidebar";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PLATFORM_ICONS = {
  tiktok: "🎵",
  instagram: "📸",
  linkedin: "💼",
  twitter: "🐦",
  youtube: "▶️"
};

export default function CampaignDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [content, setContent] = useState([]);
  const [scheduledPosts, setScheduledPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState({});
  const [selectedContent, setSelectedContent] = useState(null);

  useEffect(() => {
    fetchCampaignData();
  }, [id]);

  const fetchCampaignData = async () => {
    try {
      const [campaignRes, contentRes, postsRes] = await Promise.all([
        axios.get(`${API}/campaigns/${id}`),
        axios.get(`${API}/content/${id}`),
        axios.get(`${API}/schedule/${id}`)
      ]);
      setCampaign(campaignRes.data);
      setContent(contentRes.data);
      setScheduledPosts(postsRes.data);
    } catch (error) {
      console.error("Error fetching campaign:", error);
      toast.error("Failed to load campaign");
      navigate("/dashboard");
    } finally {
      setLoading(false);
    }
  };

  const generateScript = async (topic, platform) => {
    const key = `${topic}-${platform}-script`;
    setGenerating({ ...generating, [key]: true });
    
    try {
      const response = await axios.post(`${API}/content/generate-script`, {
        campaign_id: id,
        topic,
        platform,
        content_type: "script"
      });
      toast.success("Script generated!");
      fetchCampaignData();
    } catch (error) {
      toast.error("Failed to generate script");
    } finally {
      setGenerating({ ...generating, [key]: false });
    }
  };

  const generateImage = async (topic, platform) => {
    const key = `${topic}-${platform}-image`;
    setGenerating({ ...generating, [key]: true });
    
    try {
      const response = await axios.post(`${API}/content/generate-image`, {
        campaign_id: id,
        topic,
        platform,
        content_type: "image"
      });
      toast.success("Image generated!");
      fetchCampaignData();
    } catch (error) {
      toast.error("Failed to generate image");
    } finally {
      setGenerating({ ...generating, [key]: false });
    }
  };

  const updateStatus = async (status) => {
    try {
      await axios.patch(`${API}/campaigns/${id}/status?status=${status}`);
      toast.success(`Campaign ${status}`);
      setCampaign({ ...campaign, status });
    } catch (error) {
      toast.error("Failed to update status");
    }
  };

  const deleteCampaign = async () => {
    try {
      await axios.delete(`${API}/campaigns/${id}`);
      toast.success("Campaign deleted");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete campaign");
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      active: "status-active",
      draft: "status-draft",
      paused: "status-paused",
      completed: "status-completed"
    };
    return (
      <Badge variant="outline" className={`${styles[status] || ''} border`}>
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex bg-background">
        <Sidebar active="dashboard" />
        <main className="flex-1 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </main>
      </div>
    );
  }

  if (!campaign) return null;

  return (
    <div className="min-h-screen flex bg-background">
      <Sidebar active="dashboard" />
      
      <main className="flex-1 p-6 lg:p-8 overflow-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/dashboard")}
            data-testid="back-btn"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-2xl md:text-3xl font-bold tracking-tight">{campaign.name}</h1>
            <p className="text-muted-foreground">{campaign.company_name} • {campaign.industry}</p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(campaign.status)}
          </div>
        </div>

        {/* Action Bar */}
        <div className="flex flex-wrap gap-3 mb-8">
          {campaign.status !== 'active' ? (
            <Button
              onClick={() => updateStatus('active')}
              className="rounded-full bg-green-600 hover:bg-green-700"
              data-testid="activate-campaign-btn"
            >
              <Play className="w-4 h-4 mr-2" /> Activate Campaign
            </Button>
          ) : (
            <Button
              onClick={() => updateStatus('paused')}
              variant="outline"
              className="rounded-full"
              data-testid="pause-campaign-btn"
            >
              <Pause className="w-4 h-4 mr-2" /> Pause Campaign
            </Button>
          )}
          <Button
            variant="outline"
            className="rounded-full"
            onClick={fetchCampaignData}
            data-testid="refresh-btn"
          >
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            className="rounded-full text-destructive hover:bg-destructive/10"
            onClick={deleteCampaign}
            data-testid="delete-campaign-btn"
          >
            <Trash2 className="w-4 h-4 mr-2" /> Delete
          </Button>
        </div>

        {/* Campaign Info Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="glass-card border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Platforms</p>
              <div className="flex gap-1">
                {campaign.platforms.map((p) => (
                  <span key={p} className="text-xl">{PLATFORM_ICONS[p]}</span>
                ))}
              </div>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Frequency</p>
              <p className="font-semibold">{campaign.frequency.replace('_', ' ')}</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Duration</p>
              <p className="font-semibold">{campaign.duration_days} days</p>
            </CardContent>
          </Card>
          <Card className="glass-card border-white/5">
            <CardContent className="p-4">
              <p className="text-sm text-muted-foreground mb-1">Language</p>
              <p className="font-semibold">{campaign.language.toUpperCase()}</p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="topics" className="space-y-6">
          <TabsList className="glass border-white/10">
            <TabsTrigger value="topics" data-testid="topics-tab">Topics ({campaign.topics?.length || 0})</TabsTrigger>
            <TabsTrigger value="content" data-testid="content-tab">Content ({content.length})</TabsTrigger>
            <TabsTrigger value="schedule" data-testid="schedule-tab">Schedule ({scheduledPosts.length})</TabsTrigger>
          </TabsList>

          {/* Topics Tab */}
          <TabsContent value="topics">
            <Card className="glass-card border-white/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-primary" />
                  Generated Topics
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-4">
                  {campaign.topics?.map((topic, i) => (
                    <motion.div
                      key={i}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: i * 0.05 }}
                      className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5 hover:border-primary/30 transition-colors"
                      data-testid={`topic-${i}`}
                    >
                      <div className="flex items-center gap-4">
                        <span className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center text-sm font-mono text-primary">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        <span className="font-medium">{topic}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {campaign.platforms.map((platform) => (
                          <div key={platform} className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3"
                              onClick={() => generateScript(topic, platform)}
                              disabled={generating[`${topic}-${platform}-script`]}
                              data-testid={`gen-script-${i}-${platform}`}
                            >
                              {generating[`${topic}-${platform}-script`] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <FileText className="w-4 h-4" />
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              className="h-8 px-3"
                              onClick={() => generateImage(topic, platform)}
                              disabled={generating[`${topic}-${platform}-image`]}
                              data-testid={`gen-image-${i}-${platform}`}
                            >
                              {generating[`${topic}-${platform}-image`] ? (
                                <Loader2 className="w-4 h-4 animate-spin" />
                              ) : (
                                <Image className="w-4 h-4" />
                              )}
                            </Button>
                          </div>
                        ))}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Content Tab */}
          <TabsContent value="content">
            <Card className="glass-card border-white/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="w-5 h-5 text-primary" />
                  Generated Content
                </CardTitle>
              </CardHeader>
              <CardContent>
                {content.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No content generated yet. Generate scripts or images from the Topics tab.</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {content.map((item, i) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: i * 0.05 }}
                      >
                        <Dialog>
                          <DialogTrigger asChild>
                            <Card className="glass-card border-white/5 card-hover cursor-pointer" data-testid={`content-item-${i}`}>
                              <CardContent className="p-4">
                                <div className="flex items-center gap-2 mb-3">
                                  {item.content_type === 'script' ? (
                                    <FileText className="w-4 h-4 text-blue-400" />
                                  ) : item.content_type === 'image' ? (
                                    <Image className="w-4 h-4 text-purple-400" />
                                  ) : (
                                    <Video className="w-4 h-4 text-red-400" />
                                  )}
                                  <Badge variant="outline" className="text-xs">
                                    {item.platform_variant}
                                  </Badge>
                                </div>
                                <h4 className="font-medium truncate mb-2">{item.title}</h4>
                                {item.content_type === 'image' ? (
                                  <div className="aspect-video bg-white/5 rounded-lg overflow-hidden">
                                    <img 
                                      src={`data:image/png;base64,${item.content}`}
                                      alt={item.title}
                                      className="w-full h-full object-cover"
                                    />
                                  </div>
                                ) : (
                                  <p className="text-sm text-muted-foreground line-clamp-3">
                                    {item.content.substring(0, 150)}...
                                  </p>
                                )}
                              </CardContent>
                            </Card>
                          </DialogTrigger>
                          <DialogContent className="glass border-white/10 max-w-2xl max-h-[80vh] overflow-auto">
                            <DialogHeader>
                              <DialogTitle>{item.title}</DialogTitle>
                              <DialogDescription>
                                {item.content_type} for {item.platform_variant}
                              </DialogDescription>
                            </DialogHeader>
                            <div className="mt-4">
                              {item.content_type === 'image' ? (
                                <img 
                                  src={`data:image/png;base64,${item.content}`}
                                  alt={item.title}
                                  className="w-full rounded-lg"
                                />
                              ) : (
                                <ScrollArea className="h-[400px] rounded-lg bg-white/5 p-4">
                                  <pre className="whitespace-pre-wrap text-sm">{item.content}</pre>
                                </ScrollArea>
                              )}
                            </div>
                          </DialogContent>
                        </Dialog>
                      </motion.div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Schedule Tab */}
          <TabsContent value="schedule">
            <Card className="glass-card border-white/5">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Calendar className="w-5 h-5 text-primary" />
                  Scheduled Posts
                </CardTitle>
              </CardHeader>
              <CardContent>
                {scheduledPosts.length === 0 ? (
                  <div className="text-center py-12">
                    <p className="text-muted-foreground">No posts scheduled yet. Generate content first, then schedule it.</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {scheduledPosts.map((post, i) => (
                      <div
                        key={post.id}
                        className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/5"
                        data-testid={`scheduled-post-${i}`}
                      >
                        <div className="flex items-center gap-4">
                          <span className="text-xl">{PLATFORM_ICONS[post.platform]}</span>
                          <div>
                            <p className="font-medium">{post.platform}</p>
                            <p className="text-sm text-muted-foreground">
                              {new Date(post.scheduled_time).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <Badge variant="outline" className={post.status === 'posted' ? 'status-completed' : 'status-draft'}>
                          {post.status}
                        </Badge>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
